import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { CreditCard, Calendar, Clock, Home, RefreshCw, Shield, Users, AlertTriangle, ArrowRight } from 'lucide-react';

const PoliciesPage = () => {
  const policies = [
    {
      icon: CreditCard,
      title: "Card on File & Automatic Payment Authorization",
      content: `By saving a card on file, you authorize Gleam Guru Cleaning to automatically charge the card for completed cleaning services. Payment is processed only after service completion and admin confirmation. You will receive a receipt via email after each charge. You may update your card at any time through your client portal. Gleam Guru does not store raw card data — all payment information is securely handled through Stripe.`
    },
    {
      icon: Calendar,
      title: "Cancellation Policy",
      content: `Cancellations made 48+ hours before your scheduled appointment: No charge.
      
Cancellations made within 24–48 hours: 50% of the scheduled service fee will be charged.

Cancellations made less than 24 hours before appointment: 100% of the scheduled service fee will be charged.

Same-day cancellations: Full charge applies. We understand emergencies happen — please contact us as soon as possible, and we'll do our best to accommodate.`
    },
    {
      icon: Clock,
      title: "Rescheduling Policy",
      content: `Rescheduling is free if done 48+ hours before your appointment.

Rescheduling requests within 48 hours are subject to availability and may incur a $25 rescheduling fee.

Same-day rescheduling requests are treated as cancellations unless a cleaner is available.

To reschedule, please use your client portal or contact us directly.`
    },
    {
      icon: Home,
      title: "No-Show / Access Policy",
      content: `If our cleaner arrives and cannot access your home due to:
• Locked doors with no entry instructions
• Incorrect access codes
• Unresponsive client
• Unsafe or unsanitary conditions

A "No-Show" fee equal to 100% of the scheduled service will be charged.

Please ensure accurate access instructions are provided before your appointment. If you need to update access details, do so at least 2 hours before your scheduled service.`
    },
    {
      icon: RefreshCw,
      title: "Refund & Satisfaction Policy",
      content: `Your satisfaction is our priority. If you are not happy with your cleaning:

Contact us within 24 hours of service completion.

We will schedule a complimentary re-clean of the areas of concern within 3 business days.

If issues persist after the re-clean, a partial or full refund may be issued at our discretion.

Refund requests made more than 24 hours after service, or without prior communication, may not be honored.`
    },
    {
      icon: Shield,
      title: "Service Completion & Verification",
      content: `All services are tracked with GPS clock-in and clock-out to verify cleaner presence.

Before and after photos may be taken to document service completion.

Payment is only charged after admin review and confirmation of completed service.

You will receive a service summary and receipt after each completed appointment.`
    },
    {
      icon: Users,
      title: "Independent Contractor Model",
      content: `Gleam Guru Cleaning operates as a cleaning service platform. Cleaners are independent contractors (1099), not employees.

Each contractor is vetted, background-checked, and trained to meet Gleam Guru standards.

Contractors provide their own supplies unless otherwise arranged.

Questions about contractor policies can be directed to our support team.`
    },
    {
      icon: AlertTriangle,
      title: "Payment Disputes",
      content: `If you believe a charge is incorrect:

Contact us within 7 days of the charge date.

Provide details of the concern and any supporting documentation.

We will investigate and respond within 5 business days.

Disputing a charge directly with your bank without contacting us first may result in account suspension until resolved.`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6 drop-shadow-lg">
              Our Policies
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Clear, fair policies that protect both you and our cleaning professionals.
            </p>
          </motion.div>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Policies Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          {policies.map((policy, index) => (
            <motion.div
              key={policy.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                <CardContent className="p-0">
                  {/* Orange accent line */}
                  <div className="h-1 bg-gradient-to-r from-[#F56F05] to-[#C2410C]" />
                  
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center flex-shrink-0">
                        <policy.icon className="h-6 w-6 text-[#F56F05]" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl md:text-2xl font-bold text-zinc-900 font-heading mb-4">
                          {policy.title}
                        </h2>
                        <div className="text-zinc-600 leading-relaxed whitespace-pre-line">
                          {policy.content}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Last Updated */}
          <p className="text-center text-sm text-zinc-500 pt-8">
            Last updated: February 2026
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading drop-shadow-lg">
              Have Questions Before Booking?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Contact our team for clarification regarding our policies.
            </p>
            <Button 
              asChild
              className="bg-white text-[#F56F05] hover:bg-zinc-100 font-bold px-10 py-7 rounded-full shadow-xl hover:shadow-2xl transition-all text-lg"
            >
              <Link to="/contact" data-testid="policies-cta">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PoliciesPage;
